import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCaptures } from '../../data/queries/use-captures';
import { useCaptureLocations } from '../../data/queries/use-capture-locations';
import { useSession } from '../auth/session';
import { formatInstant } from '../../lib/timezone';
import { CAPTURE_STATUS_LABEL, formatCaptureTitle } from './capture-status';
import { CaptureFilterBar } from './CaptureFilterBar';
import {
  CAPTURE_SEARCH_DEBOUNCE_MS,
  describeActiveCaptureFilters,
  hasActiveCaptureFilters,
  NO_CAPTURE_FILTERS,
  type CaptureFilters,
} from './capture-filters';
import './captures.css';

/**
 * The Captures in one Project — M3 / S-03-02. Design export: the Capture card.
 *
 * Rendered inside Project Home rather than as its own screen, matching the
 * design's Project Home → Captures → Capture Detail path.
 *
 * **SCOPED TO ONE PROJECT TWICE OVER, and the two are doing different jobs.**
 * The query filters on `project_id` so the list shows this Project; RLS filters
 * on ownership so another user's rows are unreachable regardless. The filter is
 * not the security boundary and must never be mistaken for one.
 *
 * NOT SHOWN, deliberately: the Work Log (M6), photos (M5), and — still — the
 * AI caption, which belongs on Capture Detail and must not compete with
 * Brandon's own words in a phone-width list (D-1).
 *
 * **FILTERS ARE LIVE AS OF M8 / S08-02: Location · Date · Status, and nothing
 * else.** The Search box is S08-03 and is deliberately absent. The narrowing is
 * done by `search_captures` in the database — **this screen contains no
 * filtering logic at all**, which is what stops a browser-side engine drifting
 * away from the one the database uses.
 */
export function CapturesList({ projectId }: { projectId: string }) {
  const { profile } = useSession();

  /**
   * **A LOCATION BELONGS TO A PROJECT, SO IT CANNOT TRAVEL TO ANOTHER ONE.**
   * `unit-1207-living-room` may not exist in the next Project, and carrying it
   * across would show an empty list under a chip naming somewhere Brandon has
   * never been. Date and Status are Project-agnostic, but they are cleared with
   * it: arriving at a Project already narrowed, with no memory of choosing it,
   * is its own kind of lie about what the Project contains.
   *
   * **THE PROJECT IS HELD ALONGSIDE THE SELECTION, and the reset happens during
   * RENDER rather than in an effect.** An effect would render one frame with
   * the previous Project's filters still applied — which is exactly long enough
   * to fire a retrieval for a Location that does not exist here. This is
   * React's documented way to adjust state when a prop changes, and it is the
   * difference between "cleared" and "cleared a moment later".
   */
  const [selection, setSelection] = useState<{
    projectId: string;
    /** What is in the Search box this instant. Runs ahead of the applied query. */
    draft: string;
    filters: CaptureFilters;
  }>({ projectId, draft: '', filters: NO_CAPTURE_FILTERS });

  if (selection.projectId !== projectId) {
    setSelection({ projectId, draft: '', filters: NO_CAPTURE_FILTERS });
  }

  // ONE MECHANISM, NOT TWO. An earlier draft ALSO derived the filters through a
  // `selection.projectId === projectId ? … : NO_CAPTURE_FILTERS` ternary, which
  // quietly made the reset above redundant — a sabotage that deleted the reset
  // changed nothing, and a guard no test can break is not a guard.
  const filters = selection.filters;
  const draft = selection.draft;

  const setFilters = (next: CaptureFilters): void => {
    // Chip changes leave the Search box exactly where it is, applied and all.
    setSelection({ projectId, draft: next.query, filters: next });
  };

  /**
   * THE DEBOUNCE, AND IT IS THE ONLY THING BETWEEN A KEYBOARD AND THE DATABASE.
   *
   * `d`, `dr`, `dry`, `dryw`, `drywal` must not each become a round trip. The
   * box updates instantly — a laggy field feels broken — and only the settled
   * value becomes a query.
   *
   * The out-of-order answers this creates are handled where they actually
   * arrive, in `useCaptures`, which refuses to apply anything but the newest
   * request. A debounce narrows that window; it does not close it.
   */
  useEffect(() => {
    if (draft === filters.query) return undefined;
    const timer = setTimeout(() => {
      setSelection((current) =>
        current.draft === current.filters.query
          ? current
          : { ...current, filters: { ...current.filters, query: current.draft } },
      );
    }, CAPTURE_SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [draft, filters.query]);

  const { status, captures, error, closeMatches } = useCaptures(projectId, filters);
  const { options: locations } = useCaptureLocations(projectId);
  const timeZone = profile?.timezone ?? null;
  const narrowed = hasActiveCaptureFilters(filters);

  return (
    <section className="project-home-captures" aria-labelledby="captures-heading">
      <CaptureFilterBar
        filters={filters}
        draftQuery={draft}
        locations={locations}
        // The count is what is ON SCREEN, filtered or not — never a total the
        // list is not showing.
        count={status === 'ready' ? captures.length : null}
        onChange={setFilters}
        onQueryChange={(next) => {
          setSelection((current) => ({ ...current, draft: next }));
        }}
        onClearQuery={() => {
          // Immediate, not debounced: clearing is a decision, not typing. The
          // three chips are deliberately untouched.
          setSelection((current) => ({
            ...current,
            draft: '',
            filters: { ...current.filters, query: '' },
          }));
        }}
      />

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading Captures…
        </p>
      ) : null}

      {status === 'failed' && error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}

      {/* TWO DIFFERENT EMPTINESSES, and conflating them would be a lie about
          the Project. "No Captures yet" on a filtered list would say the
          Project is empty when it is merely narrowed; "No captures match" on an
          unfiltered one would blame a filter that is not there. */}
      {status === 'ready' && captures.length === 0 && !narrowed ? (
        <p className="projects-empty-body">No Captures yet. Tap Capture to record the first one.</p>
      ) : null}

      {status === 'ready' && captures.length === 0 && narrowed ? (
        <div className="capture-filter-noresults">
          <p className="capture-filter-noresults-title">No captures match</p>
          {/* Built from the SELECTIONS, not the results, so it stays true when
              nothing came back. Nothing here implies anything was deleted. */}
          <p className="projects-empty-body">
            {describeActiveCaptureFilters(
              filters,
              locations.find((option) => option.key === filters.locationKey)?.label ?? null,
            )}
          </p>
          <button
            type="button"
            className="capture-filter-clearall"
            onClick={() => {
              setSelection({ projectId, draft: '', filters: NO_CAPTURE_FILTERS });
            }}
          >
            Clear filters
          </button>
        </div>
      ) : null}

      {/* **CLOSE MATCHES ARE LABELLED, ONCE, ABOVE THE LIST.** The database
          decides this — every row of a fallback answer carries the flag, and a
          response is never a mixture — so the label describes the whole list
          rather than guessing per card. Brandon must never mistake a guess for
          what he asked for. */}
      {status === 'ready' && captures.length > 0 && closeMatches ? (
        <p className="capture-close-matches" role="status">
          Close matches
        </p>
      ) : null}

      {status === 'ready' && captures.length > 0 ? (
        <ul className="capture-card-list">
          {captures.map(({ capture, transcript, organization }) => (
            <li key={capture.id}>
              <Link className="capture-card" to={`/projects/${projectId}/captures/${capture.id}`}>
                <span className="capture-card-title">
                  {/* The ACCEPTED location, or the honest fallback. The list is
                      passive: it reads what organization produced and never
                      starts one — a screen that spent a model call because it
                      rendered would be a bill nobody asked for. */}
                  {formatCaptureTitle(capture.capture_number, organization?.location_display)}
                </span>
                <span className="capture-card-meta">
                  {timeZone ? formatInstant(capture.created_at, timeZone) : ''}
                  <span className={`status-chip status-chip--capture-${capture.status}`}>
                    {CAPTURE_STATUS_LABEL[capture.status]}
                  </span>
                </span>
                {/* A Capture with no transcript is valid and says so, rather
                    than rendering an empty line that reads as a broken row. */}
                <span className="capture-card-body">
                  {transcript ?? 'No transcript on this Capture.'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
